import { updateSettings } from '@redux/settings/settings.reducer';
import { HeightUnits } from '@utils/unit.utils';

import { DisplayModeEnum } from '@/types/authent/login.types';
import { CoordinatesUnitEnum } from '@/types/commons/commons.types';
import { UserSettings } from '@/types/config/settings.types';
import { MapFiltersDto } from '@/types/filters.types';

import { CONFIG_URL, configApi } from './config.api';

export const SETTINGS_TAG: { type: 'UserSettings'; id: string } = { type: 'UserSettings', id: 'USER_SETTINGS' };

export const settingApi = configApi.injectEndpoints({
  endpoints: (builder) => ({
    getUserSettings: builder.query<UserSettings | null, void>({
      query: () => ({
        url: `${CONFIG_URL}/user-settings-v5`,
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled;
        data !== null && dispatch(updateSettings(data));
      },
      providesTags: [SETTINGS_TAG],
    }),

    updateUserSettings: builder.mutation<UserSettings, UserSettings>({
      query: (userSettings) => ({
        url: `${CONFIG_URL}/user-settings-v5`,
        method: 'PUT',
        body: userSettings,
      }),
      invalidatesTags: [SETTINGS_TAG],
    }),

    patchCoordinatesUnit: builder.mutation<UserSettings, { login: string; coordinatesUnit: CoordinatesUnitEnum }>({
      query: ({ login, coordinatesUnit }) => ({
        url: `${CONFIG_URL}/user-settings-v5/${login}/coordinates-unit`,
        method: 'PATCH',
        body: coordinatesUnit,
      }),
      invalidatesTags: [SETTINGS_TAG],
    }),

    patchHeightUnit: builder.mutation<UserSettings, { login: string; heightUnit: HeightUnits }>({
      query: ({ login, heightUnit }) => ({
        url: `${CONFIG_URL}/user-settings-v5/${login}/height-unit`,
        method: 'PATCH',
        body: heightUnit,
      }),
      invalidatesTags: [SETTINGS_TAG],
    }),

    patchAltitudeUnit: builder.mutation<UserSettings, { login: string; altitudeUnit: HeightUnits }>({
      query: ({ login, altitudeUnit }) => ({
        url: `${CONFIG_URL}/user-settings-v5/${login}/altitude-unit`,
        method: 'PATCH',
        body: altitudeUnit,
      }),
      invalidatesTags: [SETTINGS_TAG],
    }),

    patchBaseLayer: builder.mutation<UserSettings, { login: string; baseLayer: number }>({
      query: ({ login, baseLayer }) => ({
        url: `${CONFIG_URL}/user-settings-v5/${login}/base-layer`,
        method: 'PATCH',
        body: JSON.stringify(baseLayer),
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: [SETTINGS_TAG],
    }),

    patchOverlays: builder.mutation<UserSettings, { login: string; overlays: number[] }>({
      query: ({ login, overlays }) => ({
        url: `${CONFIG_URL}/user-settings-v5/${login}/overlays`,
        method: 'PATCH',
        body: overlays,
      }),
      invalidatesTags: [SETTINGS_TAG],
    }),

    patchContext: builder.mutation<UserSettings, { login: string; context: string }>({
      query: ({ login, context }) => ({
        url: `${CONFIG_URL}/user-settings-v5/${login}/context`,
        method: 'PATCH',
        body: context,
      }),
      invalidatesTags: [SETTINGS_TAG],
    }),

    patchLanguage: builder.mutation<UserSettings, { login: string; language: string }>({
      query: ({ login, language }) => ({
        url: `${CONFIG_URL}/user-settings-v5/${login}/language`,
        method: 'PATCH',
        body: language,
      }),
      invalidatesTags: [SETTINGS_TAG],
    }),

    patchDisplayMode: builder.mutation<UserSettings, { login: string; displayMode: DisplayModeEnum }>({
      query: ({ login, displayMode }) => ({
        url: `${CONFIG_URL}/user-settings-v5/${login}/displayMode`,
        method: 'PATCH',
        body: JSON.stringify(displayMode),
      }),
      invalidatesTags: [SETTINGS_TAG],
    }),

    patchAxisInvertedY: builder.mutation<UserSettings, { login: string; axisInvertedY: boolean }>({
      query: ({ login, axisInvertedY }) => ({
        url: `${CONFIG_URL}/user-settings-v5/${login}/y-axis-inverted`,
        method: 'PATCH',
        body: JSON.stringify(axisInvertedY),
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: [SETTINGS_TAG],
    }),

    patchLinearJoystickCoefficient: builder.mutation<
      UserSettings,
      {
        login: string;
        linearJoystickCoefficient: number;
      }
    >({
      query: ({ login, linearJoystickCoefficient }) => ({
        url: `${CONFIG_URL}/user-settings-v5/${login}/linear-joystick-coefficient`,
        method: 'PATCH',
        body: JSON.stringify(linearJoystickCoefficient),
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: [SETTINGS_TAG],
    }),

    patchZoomJoystickCameraSensitivity: builder.mutation<
      UserSettings,
      {
        login: string;
        zoomJoystickCameraSensitivity: number;
      }
    >({
      query: ({ login, zoomJoystickCameraSensitivity }) => ({
        url: `${CONFIG_URL}/user-settings-v5/${login}/zoom-joystick-camera-sensitivity`,
        method: 'PATCH',
        body: JSON.stringify(zoomJoystickCameraSensitivity),
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: [SETTINGS_TAG],
    }),

    patchMapFilters: builder.mutation<UserSettings, { login: string; mapFilters: MapFiltersDto }>({
      query: ({ login, mapFilters }) => ({
        url: `${CONFIG_URL}/user-settings-v5/${login}/map-filters`,
        method: 'PATCH',
        body: mapFilters,
      }),
      invalidatesTags: [SETTINGS_TAG],
    }),

    patchAutomatedLad: builder.mutation<UserSettings, { login: string; automatedLad: boolean }>({
      query: ({ login, automatedLad }) => ({
        url: `${CONFIG_URL}/user-settings-v5/${login}/automated-lad`,
        method: 'PATCH',
        body: JSON.stringify(automatedLad),
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: [SETTINGS_TAG],
    }),

    patchGroupCode: builder.mutation<UserSettings, { login: string; groupCode: string }>({
      query: ({ login, groupCode }) => ({
        url: `${CONFIG_URL}/user-settings-v5/${login}/group-code`,
        method: 'PATCH',
        body: groupCode,
      }),
      invalidatesTags: [SETTINGS_TAG],
    }),

    patchHeightSuffix: builder.mutation<UserSettings, { login: string; heightSuffix: boolean }>({
      query: ({ login, heightSuffix }) => ({
        url: `${CONFIG_URL}/user-settings-v5/${login}/height-suffix`,
        method: 'PATCH',
        body: JSON.stringify(heightSuffix),
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: [SETTINGS_TAG],
    }),
  }),
});

export const {
  usePatchContextMutation,
  useGetUserSettingsQuery,
  useLazyGetUserSettingsQuery,
  useUpdateUserSettingsMutation,
  usePatchAxisInvertedYMutation,
  usePatchLinearJoystickCoefficientMutation,
  usePatchZoomJoystickCameraSensitivityMutation,
  usePatchCoordinatesUnitMutation,
  usePatchLanguageMutation,
  usePatchDisplayModeMutation,
  usePatchBaseLayerMutation,
  usePatchOverlaysMutation,
  usePatchMapFiltersMutation,
  usePatchAutomatedLadMutation,
  usePatchGroupCodeMutation,
  usePatchHeightSuffixMutation,
  usePatchAltitudeUnitMutation,
  usePatchHeightUnitMutation,
} = settingApi;
